const React = require('react');
const Storage = require('utils/storage');
const { useEffect, useState, useRef } = React;
const { default: Styled } = require('styled-components');
const { Link } = require('react-router-dom');
const { context } = require('app-context');
const { useAppSelector } = require('utils/redux');
const { getSizedImageUrl } = require('utils/image');
const DiscoverControls = require('./DiscoverControls');
const MedalGallery = require('./MedalGallery');
const WeeklyHonorIcon = require('./WeeklyHonorIcon');
const LeaderFrame = require('./LeaderFrame');
const Fonts = require('styles/fonts.json');
const Ui = require('./styles');
const { participationLabel } = require('./copy');
const LeaderboardNavigation = require('utils/leaderboardNavigation');

const internals = {};

module.exports = function Gamification({ mode = 'profile', targetId, isMe = false, onViewChange = () => {}, onWeeklyLeadersChange = () => {}, renderDiscoverControls = (props) => <DiscoverControls {...props} />, children }) {

    const user = useAppSelector(context.selectors.all.getCurrentUserObject);
    const schoolId = useAppSelector(context.selectors.all.getCurrentSchoolId);
    const scope = { schoolId, userId: user?.id, roleId: user?.roleId };
    const boardRef = useRef(null);
    const panelScope = `${schoolId}:${user?.id}:${targetId || ''}:${mode}`;
    const activePanel = useRef(panelScope);
    activePanel.current = panelScope;
    const fromLeaderboard = mode === 'portrait' && typeof window !== 'undefined' && LeaderboardNavigation.isLeaderboard(scope, window.location.search);
    const portraitPeriod = fromLeaderboard && new URLSearchParams(window.location.search).get('leaderboardPeriod') === 'allTime' ? 'allTime' : 'weekly';
    const eligible = schoolId === 240394 && user?.roleId === 2;
    const [summary, setSummary] = useState(null);
    const summaryScope = useRef(null);
    const [period, setPeriod] = useState('weekly');
    const [board, setBoard] = useState(null);
    const [showBoard, setShowBoard] = useState(false);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [allTimeStanding, setAllTimeStanding] = useState(null);
    const { Panel, Cards, Card, Score, Portrait, Footer, ProfileSection, ProfileHeading, ProfileCaption, Stats, Honors, Explanation, ParticipationToggle, InviteLink, PointsSection, VisibilityRow } = internals;

    useEffect(() => {

        let mounted = true;
        let sequence = 0;
        setSummary(null);
        setBusy(false);
        setError('');
        setBoard(null);
        setShowBoard(false);
        onViewChange(false);
        onWeeklyLeadersChange({});
        const load = async () => {

            const request = ++sequence;
            try {
                const data = await (['profile', 'portrait'].includes(mode) && !isMe ? context.api.nearpeer.gamification.profile(targetId) : context.api.nearpeer.gamification.summary());
                if (!mounted || request !== sequence) { return; }
                summaryScope.current = panelScope;
                setSummary(data);
                if (mode === 'discover' && data.enabled && !data.optedOut) {
                    const saved = LeaderboardNavigation.read(scope);
                    setPeriod(saved.period);
                    setShowBoard(saved.view);
                    onViewChange(saved.view);
                }
            }
            catch { if (mounted && request === sequence) { setError('Progress is temporarily unavailable.'); } }
        };
        const onParticipation = () => {

            LeaderboardNavigation.clearRanks(scope);
            if (eligible && ['portrait', 'footer'].includes(mode)) { setSummary(null); load(); }
        };
        if (eligible) { load(); }
        window.addEventListener('gamification-participation', onParticipation);
        const onProgress = () => { if (eligible) { load(); } };
        window.addEventListener('gamification-progress', onProgress);
        const timer = mode === 'footer' && eligible ? window.setInterval(load, 60000) : null;
        return () => { mounted = false; window.clearInterval(timer); window.removeEventListener('gamification-participation', onParticipation); window.removeEventListener('gamification-progress', onProgress); };
    }, [schoolId, user?.id, eligible, targetId, isMe, mode]);

    useEffect(() => {

        let mounted = true;

        const needsBoard = ['discover', 'decoration'].includes(mode) || (mode === 'portrait' && portraitPeriod === 'allTime' && !summary?.standings);
        if (eligible && summary?.enabled && !summary.optedOut && needsBoard) {
            const requestedPeriod = mode === 'portrait' ? portraitPeriod : showBoard ? period : 'weekly';
            setError('');
            setBoard(null);
            context.api.nearpeer.gamification.leaderboard(requestedPeriod).then((data) => {

                if (!mounted) { return; }
                LeaderboardNavigation.rememberRanks(scope, requestedPeriod, data.enabled ? data.rows || [] : []);
                setBoard({ ...data, period: requestedPeriod });
                if (['discover', 'decoration'].includes(mode) && requestedPeriod === 'weekly') {
                    onWeeklyLeadersChange(data.enabled ? Object.fromEntries((data.rows || []).filter(({ rank, points }) => rank >= 1 && rank <= 3 && points > 0).map(({ id, rank }) => [id, rank])) : {});
                }
            }).catch(() => {

                if (!mounted) { return; }
                setError('The leaderboard is temporarily unavailable.');
                if (['discover', 'decoration'].includes(mode)) { onWeeklyLeadersChange({}); }
            });
        }

        return () => { mounted = false; };
    }, [eligible, summary?.enabled, summary?.optedOut, showBoard, period, portraitPeriod, mode, targetId, schoolId, user?.id]);

    useEffect(() => {

        let current = true;
        setAllTimeStanding(null);
        if (eligible && mode === 'profile' && isMe && summary?.enabled && !summary.optedOut) {
            context.api.nearpeer.gamification.leaderboard('allTime').then((data) => {

                if (current && data.enabled) {
                    setAllTimeStanding({ rank: data.rows?.find(({ id, points }) => id === user?.id && points > 0)?.rank || null });
                }
            }).catch(() => current && setAllTimeStanding({ unavailable: true }));
        }
        return () => { current = false; };
    }, [eligible, mode, isMe, summary?.enabled, summary?.optedOut, user?.id, schoolId]);

    useEffect(() => {

        if (showBoard && board && boardRef.current) {
            boardRef.current.scrollTop = LeaderboardNavigation.read(scope).scrollTop;
        }
    }, [board, showBoard]);

    if (mode === 'portrait') {
        if (!eligible) { return children; }
        const progress = summaryScope.current === panelScope && summary?.enabled && !summary.optedOut ? summary : null;
        const cachedRank = !error && !(summaryScope.current === panelScope && summary) ? LeaderboardNavigation.recentRank(scope, portraitPeriod, targetId || user?.id) : null;
        const selectedRank = progress?.standings ? progress.standings[portraitPeriod]?.rank : portraitPeriod === 'allTime' ? board?.period === 'allTime' ? board.rows?.find(({ id, points }) => id === (targetId || user?.id) && points > 0)?.rank : null : progress?.weeklyRank;
        const rank = progress ? selectedRank : cachedRank;
        const metal = internals.leaderMetals[rank];
        const allTimeRank = progress?.standings?.allTime?.rank || (portraitPeriod === 'allTime' ? rank : null);
        const weeklyRank = progress?.standings?.weekly?.rank || progress?.weeklyRank;
        const captions = !progress ? [] : isMe ? [
            `${allTimeRank ? `#${allTimeRank} all time` : 'All time'} (${Number(progress.totalPoints || 0).toLocaleString()} points)`,
            `${weeklyRank ? `#${weeklyRank} this week` : 'This week'} (${Number(progress.weeklyPoints || 0).toLocaleString()} points)`
        ] : fromLeaderboard ? [
            allTimeRank ? `#${allTimeRank} all-time` : 'Not ranked all-time',
            weeklyRank ? `#${weeklyRank} this week` : 'Not ranked this week'
        ] : progress.standings ? [
            progress.standings.allTime?.label && `${progress.standings.allTime.label} all-time`,
            progress.standings.weekly?.label && `${progress.standings.weekly.label} this week`
        ].filter(Boolean) : rank ? [`#${rank} on ${portraitPeriod === 'allTime' ? 'the all-time leaderboard' : 'this week’s leaderboard'}`] : [];
        // Reuse a recent leaderboard rank so its frame is present on the first paint.
        // Keep the photo mounted and full size while standings load. The frame
        // paints outside its bounds instead of taking space from the image.
        // Figures also avoid the profile's legacy depth-based div selectors.
        return <figure id={isMe ? 'gamification-standing-summary' : undefined} ref={(element) => {

            if (isMe && element && progress && window.location.hash === '#gamification-standing-summary') { element.scrollIntoView?.({ block: 'center' }); }
        }} style={{ margin: 0 }}>
            <LeaderFrame as='figure' $outside $metal={metal} data-leader-frame={metal} style={{ margin: 0, maxWidth: 'none', borderRadius: 27 }}>{children}<span className='sparkle sparkle-top' aria-hidden='true'>✦</span><span className='sparkle sparkle-bottom' aria-hidden='true'>✧</span></LeaderFrame>
            {!!captions.length && <ProfileCaption>{captions.map((caption) => <span key={caption} style={{ display: 'block' }}>{caption}</span>)}</ProfileCaption>}
        </figure>;
    }

    if (!eligible || !summary?.enabled) {
        return mode === 'portrait' ? children : mode === 'discover' ? renderDiscoverControls({ enabled: false }) : null;
    }

    if (mode === 'footer') {
        return summary.optedOut ? null : <Link to={`/app/profile/${user.id}#gamification-standing-summary`} aria-label={`${Number(summary.totalPoints || 0).toLocaleString()} points, view your standings`} onClick={() => window.requestAnimationFrame(() => document.getElementById('gamification-standing-summary')?.scrollIntoView?.({ block: 'center' }))}>{Number(summary.totalPoints || 0).toLocaleString()} pts</Link>;
    }

    const changeParticipation = async (optedOut) => {

        const savingPanel = activePanel.current;

        setBusy(true);
        setError('');

        try {
            await context.api.nearpeer.gamification.participation(optedOut);
            if (activePanel.current !== savingPanel) { return; }
            window.dispatchEvent(new Event('gamification-participation'));
            setSummary((previous) => ({ ...previous, optedOut }));
            // Refresh only this panel's values; keep it mounted while saving.
            try {
                const fresh = await context.api.nearpeer.gamification.summary();
                if (activePanel.current === savingPanel) { setSummary(fresh); }
            }
            catch (refreshError) { /* The preference was saved; retain the current panel. */ }
        }
        catch (err) {
            if (activePanel.current === savingPanel) { setError('Unable to save your preference. Please try again.'); }
        }
        finally {
            if (activePanel.current === savingPanel) { setBusy(false); }
        }
    };

    if (mode === 'decoration') { return null; }


    const participationToggle = isMe ? <ParticipationToggle
        type='button'
        role='switch'
        aria-label={participationLabel}
        aria-checked={!summary.optedOut}
        disabled={busy}
        onClick={(event) => {

            event.preventDefault();
            event.stopPropagation();
            changeParticipation(!summary.optedOut);
        }}
    ><span aria-hidden='true' className='switch-track' /><span className='switch-state'>{summary.optedOut ? 'Off' : 'On'}</span><span>{participationLabel}</span></ParticipationToggle> : null;

    if (summary.optedOut && !(mode === 'profile' && isMe)) {
        return mode === 'discover' ? renderDiscoverControls({ enabled: false }) : null;
    }

    if (mode === 'discover') {
        const rows = (board?.rows || []).filter(({ points }) => points > 0);
        return <>
            {renderDiscoverControls({ enabled: true, showBoard, period, onPeriodChange: (nextPeriod) => {

                LeaderboardNavigation.write(scope, { period: nextPeriod, scrollTop: 0, ids: [] });
                setBoard(null);
                setPeriod(nextPeriod);
            }, onToggle: (show) => {

                LeaderboardNavigation.write(scope, { view: show });
                setError('');
                setShowBoard(show);
                onViewChange(show);
            } })}
            {showBoard && <Panel $scrollable ref={boardRef} onScroll={(event) => LeaderboardNavigation.write(scope, { scrollTop: event.currentTarget.scrollTop })}>
                {!board && !error && <p role='status'>Loading leaderboard…</p>}
                <Cards>{rows.map((row, index) => <Card key={row.id}>
                    <Link to={LeaderboardNavigation.profileUrl(row.id, index, period)} onClick={() => LeaderboardNavigation.write(scope, { view: true, period, ids: rows.map(({ id }) => id), scrollTop: boardRef.current?.scrollTop || 0 })}>
                        {!!row.croppedPicture && <LeaderFrame $metal={internals.leaderMetals[row.rank]} data-leader-frame={internals.leaderMetals[row.rank]}><Portrait src={getSizedImageUrl(row.croppedPicture, 400)} alt='' />{internals.leaderMetals[row.rank] && <><span className='sparkle sparkle-top' aria-hidden='true'>✦</span><span className='sparkle sparkle-bottom' aria-hidden='true'>✧</span><span className='leader-label'>{internals.leaderMetals[row.rank]} leader</span></>}</LeaderFrame>}
                        <Score>#{row.rank}: {row.points} points</Score>
                        <strong>{row.firstName} {row.lastName}{row.isMe ? ' (you)' : ''}</strong>
                    </Link>
                </Card>)}</Cards>
                {board && rows.length === 0 && <p>{period === 'weekly' ? 'No points earned this week yet.' : 'No points earned yet.'}</p>}
                {!!error && <p role='alert'>{error}</p>}
            </Panel>}
            {showBoard && period === 'weekly' && <Footer>Weeks run Monday–Sunday in the school’s time zone.</Footer>}
        </>;
    }

    const expanded = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('achievements') === '1';

    const disclosureKey = `gamification:${schoolId}:${user?.roleId}:${user?.id}:points-medals`;
    return <ProfileSection><PointsSection key={panelScope} storageKey={disclosureKey} defaultOpen={expanded}>
        <ProfileHeading><h3>Points &amp; medals</h3><span className='section-toggle'><span className='when-closed'>Show</span><span className='when-open'>Hide</span><span aria-hidden='true'>⌄</span></span></ProfileHeading>
        {isMe && <VisibilityRow>{participationToggle}</VisibilityRow>}
        {!summary.optedOut && <>
            <Stats aria-label='Points summary'>
                <li><strong>{summary.totalPoints}</strong> all-time points</li>
                {isMe && <>
                    <li><strong>{summary.weeklyPoints}</strong> {summary.weeklyPoints === 1 ? 'point' : 'points'} this week</li>
                    <li><strong>{summary.streak.current}</strong>-day current streak</li>
                    <li><strong>{summary.streak.longest}</strong>-day longest streak</li>
                    <li>{summary.weeklyRank ? <><strong>#{summary.weeklyRank}</strong> current weekly ranking</> : 'Not ranked this week yet'}</li>
                    <li>{!allTimeStanding ? 'Loading all-time ranking…' : allTimeStanding.unavailable ? 'All-time ranking temporarily unavailable' : allTimeStanding.rank ? <><strong>#{allTimeStanding.rank}</strong> all-time ranking</> : 'Not ranked all-time yet'}</li>
                </>}
            </Stats>
            <MedalGallery key={`${schoolId}-${targetId || user?.id}`} medals={summary.medals} isMe={isMe} />
            {isMe && <InviteLink><Link to={{ pathname: '/app/invite-friends', state: { fromProfile: `/app/profile/${user.id}` } }}>Invite your friends</Link></InviteLink>}
            {(isMe || !!summary.weeklyHonors?.length) && <Honors>
                <h4>Weekly honors</h4>
                <p>Weekly honors recognize Top {summary.honorCutoff || 10} leaderboard finishes after each week ends.</p>
                {isMe && <Explanation>Earn points daily through activities such as adding interests and passions, connecting with peers, joining groups, chatting, and completing surveys.</Explanation>}
                {!summary.weeklyHonors?.length && <p>No weekly honors yet. Your qualifying weekly finishes will appear here after each week ends.</p>}
                {!!summary.weeklyHonors?.length && <>
                    <h5>Past finishes</h5>
                    <ul aria-label='Weekly finishes'>{summary.weeklyHonors.map((honor) => <li key={honor.weekStart}><WeeklyHonorIcon rank={honor.rank} /> <strong>#{honor.rank}</strong>: {internals.honorDates(honor.weekStart)} ({honor.points} points)</li>)}</ul>
                </>}
            </Honors>}
        </>}
        {isMe && !!error && <p role='alert'>{error}</p>}
    </PointsSection></ProfileSection>;

};

internals.PointsSection = function PointsSection({ storageKey, defaultOpen, children }) {

    const [open, setOpen] = useState(() => {

        try {
            const saved = storageKey && Storage.getItem(storageKey);
            return saved !== null && saved !== undefined ? saved === 'true' : !!defaultOpen;
        }
        catch { return !!defaultOpen; }
    });
    return <details open={open} onToggle={(event) => {

        const next = event.currentTarget.open;
        setOpen(next);
        if (storageKey) {
            try { Storage.setItem(storageKey, String(next)); }
            catch { /* Disclosure still works when storage is unavailable. */ }
        }
    }}>{children}</details>;
};

internals.VisibilityRow = Styled.div`margin: 16px 0 12px; text-align: left;`;

internals.Panel = Styled.section`
    ${Ui.surface}
    ${(props) => props.$scrollable ? 'flex: 1 1 auto; min-height: 0; overflow-y: auto;' : ''}
    margin: 16px 0; padding: ${(props) => props.$scrollable ? 12 : 20}px;
    summary, select, button { cursor: pointer; }
    select { padding: 8px; border-radius: 6px; margin-left: 8px; background: ${Ui.paper}; color: ${Ui.text}; }
    summary { padding: 8px 0; }
`;
internals.Cards = Styled.div`display: flex; flex-wrap: wrap; gap: 12px; margin: 12px 0;`;
internals.Card = Styled.div`box-sizing: border-box; flex: 0 1 calc((100% - 12px) / 2); min-width: 0; max-width: 280px; padding: 8px; border-radius: 12px; background: ${Ui.paper}; border: 1px solid ${Ui.divider}; text-align: center; a { color: inherit; text-decoration: none; } strong { display: block; font-size: 14px; line-height: 1.25; overflow-wrap: anywhere; } ${LeaderFrame} { max-width: 220px; }`;
internals.Score = Styled.p`font-size: 14px; line-height: 1.25; font-weight: 700; margin: 6px 0 4px;`;
internals.Muted = Styled.p`color: ${Ui.secondaryText}; font-size: 14px; line-height: 1.5;`;
internals.Portrait = Styled.img`width: 100%; max-width: 180px; height: auto; aspect-ratio: 1; border-radius: 12px; object-fit: cover;`;
internals.Footer = Styled.p`flex: 0 0 auto; margin: 0 12px 12px; color: ${Ui.secondaryText}; font-size: 12px; line-height: 1.4; text-align: center;`;

internals.ProfileSection = Styled.section`${Ui.body}margin: 24px 0; color: inherit; background: transparent; p, li { font-size: inherit; line-height: 1.5; } li { margin-bottom: 12px; } h3 { font-size: 18px; } summary { cursor: pointer; } > details > summary .when-open { display: none; } > details[open] > summary .when-open { display: inline; } > details[open] > summary .when-closed { display: none; }`;
internals.ProfileHeading = Styled.summary`display: flex; align-items: center; justify-content: flex-start; list-style: none; gap: 16px; flex-wrap: wrap; padding: 0; &::-webkit-details-marker { display: none; } && > h3 { font-family: ${Fonts.headerFont}; font-size: 28px; font-weight: 700; margin: 0; } .section-toggle { display: flex; align-items: center; margin-left: auto; gap: 6px; color: ${Ui.secondaryText}; font-size: 14px; font-weight: 400; }`;

internals.leaderMetals = { 1: 'Gold', 2: 'Silver', 3: 'Bronze' };

internals.honorDates = (start) => {

    const first = new Date(`${start}T12:00:00Z`);
    const last = new Date(first.getTime() + 6 * 86400000);
    const options = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
    return `${first.toLocaleDateString('en-US', options)} – ${last.toLocaleDateString('en-US', options)}`;
};

internals.ProfileCaption = Styled.figcaption`margin: 12px 0 0; color: ${Ui.secondaryText}; font: inherit; font-size: 14px; line-height: 1.5; text-align: center;`;

internals.Stats = Styled.ul`&& { font-family: ${Fonts.copyFont}; list-style: none; padding: 0; margin: 16px 0; } && li { font-size: 16px; font-weight: 400; line-height: 1.5; margin: 0 0 6px; } && strong { font-weight: 700; }`;
internals.Honors = Styled.section`&& { font-family: ${Fonts.copyFont}; margin: 20px 0; } && h4 { font-size: 18px; margin: 0 0 10px; font-weight: 700; } && h5 { font-size: 16px; margin: 16px 0 8px; font-weight: 700; } && ul { list-style: none; padding: 0; } && p { font-size: 14px; font-weight: 400; line-height: 1.5; } && li, && summary { font-size: 16px; font-weight: 400; line-height: 1.5; margin-bottom: 6px; }`;



internals.ParticipationToggle = Styled.button`
    display: inline-flex; align-items: center; gap: 8px; padding: 6px 0;
    border: 0; background: transparent; color: inherit; font: inherit; font-size: 16px; text-align: left; cursor: pointer;
    &:disabled { opacity: .5; cursor: wait; }
    .switch-state { min-width: 24px; font-size: 14px; }
    .switch-track { flex: 0 0 32px; width: 32px; height: 18px; border-radius: 12px; background: ${Ui.secondaryText}; position: relative; }
    .switch-track::after { content: ''; position: absolute; top: 3px; left: 3px; width: 12px; height: 12px; border-radius: 50%; background: ${Ui.paper}; }
    &[aria-checked='true'] .switch-track { background: ${Ui.primary}; }
    &[aria-checked='true'] .switch-track::after { left: 17px; }
`;

internals.Explanation = Styled.p`&& { font-size: 14px; font-weight: 400; line-height: 1.5; margin: 20px 0; }`;

internals.InviteLink = Styled.p`&& { font-family: ${Fonts.copyFont}; font-size: 16px; font-weight: 400; line-height: 1.5; margin: 16px 0; } && a { font: inherit; }`;
